import { useLazyQuery } from '@apollo/client/react';
import loadable from '@loadable/component';
import { type FunctionComponent, useState } from 'react';
import { type ProjectsQuery, type ProjectsQueryVariables } from '../../../__generated__/graphql-client-types';
import { FEATURE_FLAGS } from '../../../constants/general';
import { ACCOUNT_PROJECTS_LINK, PROJECT_INTERNAL_DASHBOARD_LINK } from '../../../constants/links';
import { buildGTMHeaderProjectsClick, buildGTMHeaderNoProjectsClick } from '../../../helpers/analytics/gtm/event-builders';
import { handleKeys } from '../../../helpers/keyboard/keyboard.helper';
import { useTrackEvent } from '../../../hooks/analytics/analytics.hooks';
import { useEmployee } from '../../../hooks/apollo/employee/employee.hooks';
import { useFeature } from '../../../hooks/features/features.hooks';
import { useNavigation } from '../../../hooks/navigation/navigation.hooks';
import { useRouteChange } from '../../../hooks/routing/route-change.hook';
import { FETCH_HEADER_PROJECTS } from '../../../queries/project/project-header.queries';
import { ClickableElement } from '../../buttons';
import { IconWithLabelButton } from '../../buttons/icon-with-label-button/icon-with-label-button.component';
import { Overlay } from '../../common-components/overlay/overlay.component';
import ArrowDropDownIcon from '../../svgs/arrow-drop-down.svg';
import ListAltIcon from '../../svgs/list-alt.svg';

const LoadableHeaderDropdown = loadable(
	() => import(/* webpackChunkName: "header-dropdown.component" */ './header-dropdown/header-dropdown.component'),
	{
		resolveComponent: ({ HeaderDropdown }) => HeaderDropdown
	}
);

const ButtonContent: FunctionComponent<{ toggled?: boolean }> = ({ toggled = false }) => (
	<div className="flex bg-theme-white">
		<div
			data-testid="button-content"
			className={`flex flex-row flex-nowrap items-center f6 b pv0 ${
				toggled ? 'header-dropdown-open theme-primary-darker' : 'header-dropdown-closed theme-primary-dark'
			}`}>
			<p className="truncate mv0">
				<span className="dn dib-l">My</span> Projects
			</p>
			<ArrowDropDownIcon className="dn dib-l f4-ns" />
		</div>
	</div>
);

export const ProjectHeaderDropdown: FunctionComponent = () => {
	const navigate = useNavigation();
	const trackEvent = useTrackEvent();
	const { employee } = useEmployee();
	const hasAssociateDashboardAccess = useFeature(FEATURE_FLAGS.INTERNAL_ASSOCIATE_DASHBOARD);
	const [toggled, setToggled] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [loadProjects, { data }] = useLazyQuery<ProjectsQuery, ProjectsQueryVariables>(FETCH_HEADER_PROJECTS);
	const projects = data?.projects?.projects || [];

	useRouteChange(() => {
		// close dropdown on route change
		setToggled(false);
	});

	const handleClick = () => {
		if (projects?.length) {
			setLoading(true);
			setToggled(true);
		}
		void loadProjects({
			variables: {
				input: {
					paging: {
						pageSize: 3
					},
					sortOrder: 'DESC',
					sortColumn: 'LAST_MODIFIED',
					ignorePermissions: true,
					includeShoppingListGroups: false,
					includedRelationTypeList: ['OWNER', 'SHARED_WITH']
				}
			}
		})
			.then((response) => {
				const fetchedProjects = response?.data?.projects?.projects;
				if (fetchedProjects?.length) {
					trackEvent(buildGTMHeaderProjectsClick(null));
					setToggled(true);
				} else {
					trackEvent(buildGTMHeaderNoProjectsClick());
					if (employee.isAuthenticated && hasAssociateDashboardAccess) {
						navigate(PROJECT_INTERNAL_DASHBOARD_LINK);
					} else {
						navigate(ACCOUNT_PROJECTS_LINK);
					}
				}
			})
			.finally(() => setLoading(false));
	};

	return (
		<div className="relative pt0-ns pl0-ns mw5 w-100 w-auto-ns">
			<ClickableElement
				className="h-100 dn db-ns"
				onClick={handleClick}
				onKeyDown={handleKeys(['Enter'], handleClick)}
				ariaLabel="my-projects-dropdown-btn">
				<ButtonContent toggled={toggled} />
			</ClickableElement>
			<div className="db dn-ns">
				<IconWithLabelButton
					label="Projects"
					ariaLabel="my-projects-dropdown-btn"
					icon={<ListAltIcon className="f5 theme-primary" />}
					onClick={handleClick}
					labelClass="f7 theme-primary"
					buttonClass="pv2"
				/>
			</div>
			{toggled && projects?.length ? (
				<>
					<LoadableHeaderDropdown isLoading={loading} projects={projects} />
					<Overlay onClick={() => setToggled(false)} style={'neutral'} />
				</>
			) : null}
		</div>
	);
};
